import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { AddOnsPrompt } from './add-ons-prompt';
import { BookingSummaryTile } from './booking-summary-tile';
import { DoneButtonBlock } from './done-button';
import { TransactionDetails } from 'store/transactions/types';
import {
  ConfirmedBody,
  ConfirmedFooter,
  ConfirmedHeader,
  ConfirmedHeading,
  ConfirmedIcon,
  ConfirmedScreen,
} from './styles';

interface BookingConfirmedProps {
  booking: TransactionDetails;
  /* Decided by `Receipt`; this screen does not re-ask, or the two answers fight. */
  bookingId: string;
  /* Opens the full receipt in place — a local toggle on `Receipt`, not a route. */
  onOpenReceipt: VoidFunction;
  onBrowseAddOns: (bookingId: string) => void;
  onClose: VoidFunction;
}

/*
 * What a tenant sees after booking a room that offers add-ons. It REPLACES the receipt here rather
 * than decorating it: the receipt is a dense operational page, and a card hung off the end of it put
 * the offer below the fold. A room with no add-ons never reaches here.
 *
 * The design's hero photograph and "Most booked" badge are not built: `AddOn` carries `image_url` per
 * ITEM, and nothing carries a per-booking hero or any popularity signal.
 */
export const BookingConfirmed = ({
  booking,
  bookingId,
  onOpenReceipt,
  onBrowseAddOns,
  onClose,
}: BookingConfirmedProps): JSX.Element => {
  const intl = useIntl();

  /* The swap unmounts whatever held focus, so without this a screen reader is told nothing. */
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <ConfirmedScreen data-testid="booking-confirmed-screen">
      <ConfirmedBody>
        <ConfirmedHeader>
          {/* Decorative: the heading beside it carries the meaning, and RCL ships this icon bare. */}
          <ConfirmedIcon aria-hidden="true" />
          {/* tabIndex -1: programmatically focusable, deliberately NOT in the tab order. */}
          <ConfirmedHeading ref={headingRef} tabIndex={-1}>
            {intl.formatMessage({ id: 'booking_confirmed' })}
          </ConfirmedHeading>
        </ConfirmedHeader>

        <BookingSummaryTile booking={booking} onClick={onOpenReceipt} />

        <AddOnsPrompt bookingId={bookingId} onBrowseAddOns={onBrowseAddOns} />
      </ConfirmedBody>

      <ConfirmedFooter>
        <DoneButtonBlock onClick={onClose} text={intl.formatMessage({ id: 'done' })} />
      </ConfirmedFooter>
    </ConfirmedScreen>
  );
};
